/*
 * "Delete this message?" — the one destructive thing a row offers, and the only one that asks first
 * (spec 08 §4). The dialog decides nothing: it holds the message it was opened for and hands it
 * back, so the deletion itself stays with the surface that owns the conversation's dependencies.
 *
 * An administrator removing somebody else's message reads the same dialog with one sentence more:
 * the notice everybody else is left with names an administrator, and what that notice says is the
 * part of the act that the person deleting cannot take back (G-36).
 */

import { t as coreT } from "../../../core/i18n/index.ts"
import { Button, Dialog } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import type { MessageView } from "../wire/views.ts"

export interface DeleteConfirmProps {
	/** The message to delete, or null while the dialog is closed. */
	message: MessageView | null
	/** True while an administrator is removing somebody else's message rather than their own. */
	asAdmin?: boolean
	onCancel: () => void
	onConfirm: (msg: MessageView) => void
}

export function DeleteConfirm({ message, asAdmin, onCancel, onConfirm }: DeleteConfirmProps) {
	const body =
		asAdmin === true
			? `${t("chat.message.deleteConfirm.body")} ${t("chat.message.deleteConfirm.adminNotice")}`
			: t("chat.message.deleteConfirm.body")
	return (
		<Dialog
			open={message !== null}
			onOpenChange={(open) => {
				if (!open) onCancel()
			}}
			title={t("chat.message.deleteConfirm.title")}
			description={body}
			footer={
				<div className="tb-dialog__actions">
					<Button variant="neutral" label={coreT("action.cancel")} onClick={onCancel} />
					<Button
						variant="danger"
						label={t("chat.message.deleteConfirm.confirm")}
						onClick={() => {
							if (message) onConfirm(message)
						}}
					/>
				</div>
			}
		/>
	)
}
